import { Component, OnInit, OnDestroy, Input } from '@angular/core';
import { FormGroup, Validators, FormControl } from '@angular/forms';

import { BehaviorSubject, Subscription, tap, combineLatest, Subject, filter } from 'rxjs';

import { MessageService } from '@shared/services/message-service';

import { ActivityBlockingService } from '@shared/services/activity-blocking.service';
import { SortActivity } from '@shared/models/sort-activity';
import { ShiftStatus } from '@shared/models/worked-shift';
import { CustomValidators } from '@shared/helpers/custom-validators';

import { ProductService } from '../../../../services/product.service';
import { InventoryService } from '../../../../services/inventory.service';
import { ShiftContextService } from '../../../../services/shift-context.service';
import { InvCountPlantInfo } from '../../../../models/inv-count-plant-info';
import { saveDebounce } from '@shared/helpers/save-debounce';

@Component({
    selector: 'app-chep-misc-info',
    templateUrl: './chep-misc-info.component.html',
    styleUrls: ['./chep-misc-info.component.scss']
})
export class ChepMiscInfoComponent implements OnInit, OnDestroy {

    @Input() set subinventoryId(subinventoryId: number) {
        this.siteSubinventoryId.next(subinventoryId);
    }

    invCountPlantInfo: InvCountPlantInfo;

    sortActivityTypes: SortActivity[]= [];
    subscriptions: Subscription[];
    shutdown: Subject<void> = new Subject<void>();
    chepMiscInfoForm = new FormGroup({
        cbRepairsAwaitingPrep: new FormControl<number>(0, [ Validators.required, CustomValidators.numeric(false) ]),
        cbRepairsPrepped: new FormControl<number>(0, [ Validators.required, CustomValidators.numeric(false) ]),
        cbRepairPrepHours: new FormControl<string>('00:00', Validators.required),
        cbRepairsReady: new FormControl<number>(0, [ Validators.required, CustomValidators.numeric(false) ]),
        repairHoursUsedForTdwn: new FormControl<string>('00:00', Validators.required),
        palletsWashed: new FormControl<number>(0, [ Validators.required, CustomValidators.numeric(false) ]),
        palletsRewashed: new FormControl<number>(0, [ Validators.required, CustomValidators.numeric(false) ]),
        stackerRepairCount: new FormControl<number>(0, [ Validators.required, CustomValidators.numeric(false) ]),
        forkliftRepairCount: new FormControl<number>(0, [ Validators.required, CustomValidators.numeric(false) ]),
        multicolorArMediaBlasted: new FormControl<number>(0, [ Validators.required, CustomValidators.numeric(false) ]),
        multicolorRuMediaBlasted: new FormControl<number>(0, [ Validators.required, CustomValidators.numeric(false) ]),
        directionalRepTotalSort: new FormControl<number>(0, [ Validators.required, CustomValidators.numeric(false) ]),

        nmToPlus: new FormControl<number>(0, [ Validators.required, CustomValidators.numeric(false) ]),
        nmToPrem: new FormControl<number>(0, [ Validators.required, CustomValidators.numeric(false) ]),
        nmToCust: new FormControl<number>(0, [ Validators.required, CustomValidators.numeric(false) ]),
        repPlusToPrem: new FormControl<number>(0, [ Validators.required, CustomValidators.numeric(false) ]),
        repPlusToCust: new FormControl<number>(0, [ Validators.required, CustomValidators.numeric(false) ]),
        repPremToCust: new FormControl<number>(0, [ Validators.required, CustomValidators.numeric(false) ]),
        srtPlusToPrem: new FormControl<number>(0, [ Validators.required, CustomValidators.numeric(false) ]),
        srtPlusToCust: new FormControl<number>(0, [ Validators.required, CustomValidators.numeric(false) ]),
        srtPremToCust: new FormControl<number>(0, [ Validators.required, CustomValidators.numeric(false) ])
    });
    autoSaveFormSubscriptions: Subscription[];
    chepMiscInfoIsLoaded = false;
    chepMiscInfoIsSaved = false;
    siteSubinventoryId = new BehaviorSubject<number>(null);

    constructor(
        private inventoryService: InventoryService,
        private productService: ProductService,
        private activityService: ActivityBlockingService,
        private shiftContextService: ShiftContextService,
        private messageService: MessageService) {
    }

    ngOnInit() {
        const validSortActivities = [
            SortActivity.PlusActivity,
            SortActivity.PremActivity,
            SortActivity.CustActivity,
            SortActivity.BlockRepairs
        ]
        const sortActivityTypesObservable = this.productService.SortActivityTypesForSubinventory(this.siteSubinventoryId)
            .pipe(tap(sortActivityTypes => {
                this.sortActivityTypes = sortActivityTypes.filter(x => validSortActivities.some(t => t === x));
            }));

        const siteObservable = this.shiftContextService.CurrentContext.pipe(tap(context => {
            if (context.status !== ShiftStatus.Open)
                this.chepMiscInfoForm.disable({ emitEvent: false });
            else
                this.chepMiscInfoForm.enable({ emitEvent: false });
        }));

        const chepMiscInfoObservable = this.inventoryService.CurrentPlantInfo.pipe(
            filter(info => !!info),
            tap(info => {
                this.invCountPlantInfo = info;
                this.setFormValues(info);
            }));

        this.subscriptions = [
            combineLatest([siteObservable, sortActivityTypesObservable])
                .pipe(this.activityService.Wait('CHEP Misc Info'))
                .subscribe(),

            chepMiscInfoObservable.subscribe(),

            this.chepMiscInfoForm.valueChanges
            .pipe(saveDebounce(this.shutdown))
            .subscribe(_ => this.saveChepMiscInfo())
        ];
    }

    ngOnDestroy() {
        this.shutdown.next();
        this.subscriptions.forEach(s => s.unsubscribe());
    }

    setFormValues(chepMiscInfo: InvCountPlantInfo): void {
        this.chepMiscInfoForm.setValue({
            cbRepairsAwaitingPrep: chepMiscInfo.cbRepairsAwaitingPrep,
            cbRepairsPrepped: chepMiscInfo.cbRepairsPrepped,
            cbRepairPrepHours: chepMiscInfo.cbRepairPrepHours,
            cbRepairsReady: chepMiscInfo.cbRepairsReady,
            repairHoursUsedForTdwn: chepMiscInfo.repairHoursUsedForTdwn,
            palletsWashed: chepMiscInfo.palletsWashed,
            palletsRewashed: chepMiscInfo.palletsRewashed,
            stackerRepairCount: chepMiscInfo.stackerRepairCount,
            forkliftRepairCount: chepMiscInfo.forkliftRepairCount,
            multicolorArMediaBlasted: chepMiscInfo.multicolorArMediaBlasted,
            multicolorRuMediaBlasted: chepMiscInfo.multicolorRuMediaBlasted,
            directionalRepTotalSort: chepMiscInfo.directionalRepTotalSort,
            nmToPlus: chepMiscInfo.nmToPlus,
            nmToPrem: chepMiscInfo.nmToPrem,
            nmToCust: chepMiscInfo.nmToCust,
            repPlusToPrem: chepMiscInfo.repPlusToPrem,
            repPlusToCust: chepMiscInfo.repPlusToCust,
            repPremToCust: chepMiscInfo.repPremToCust,
            srtPlusToPrem: chepMiscInfo.srtPlusToPrem,
            srtPlusToCust: chepMiscInfo.srtPlusToCust,
            srtPremToCust: chepMiscInfo.srtPremToCust
        }, {
            emitEvent: false
        });
    }

    getFormValues(): InvCountPlantInfo {
        const update: InvCountPlantInfo = { ...this.invCountPlantInfo };

        update.cbRepairsAwaitingPrep = this.chepMiscInfoForm.controls.cbRepairsAwaitingPrep.value;
        update.cbRepairsPrepped = this.chepMiscInfoForm.controls.cbRepairsPrepped.value;
        update.cbRepairPrepHours = this.chepMiscInfoForm.controls.cbRepairPrepHours.value;
        update.cbRepairsReady = this.chepMiscInfoForm.controls.cbRepairsReady.value;
        update.repairHoursUsedForTdwn = this.chepMiscInfoForm.controls.repairHoursUsedForTdwn.value;
        update.palletsWashed = this.chepMiscInfoForm.controls.palletsWashed.value;
        update.palletsRewashed = this.chepMiscInfoForm.controls.palletsRewashed.value;
        update.stackerRepairCount = this.chepMiscInfoForm.controls.stackerRepairCount.value;
        update.forkliftRepairCount = this.chepMiscInfoForm.controls.forkliftRepairCount.value;
        update.multicolorArMediaBlasted = this.chepMiscInfoForm.controls.multicolorArMediaBlasted.value;
        update.multicolorRuMediaBlasted = this.chepMiscInfoForm.controls.multicolorRuMediaBlasted.value;
        update.directionalRepTotalSort = this.chepMiscInfoForm.controls.directionalRepTotalSort.value;

        update.nmToPlus = this.chepMiscInfoForm.controls.nmToPlus.value;
        update.nmToPrem = this.chepMiscInfoForm.controls.nmToPrem.value;
        update.nmToCust = this.chepMiscInfoForm.controls.nmToCust.value;

        update.repPlusToPrem = this.chepMiscInfoForm.controls.repPlusToPrem.value;
        update.repPlusToCust = this.chepMiscInfoForm.controls.repPlusToCust.value;
        update.repPremToCust = this.chepMiscInfoForm.controls.repPremToCust.value;

        update.srtPlusToPrem = this.chepMiscInfoForm.controls.srtPlusToPrem.value;
        update.srtPlusToCust = this.chepMiscInfoForm.controls.srtPlusToCust.value;
        update.srtPremToCust = this.chepMiscInfoForm.controls.srtPremToCust.value;

        return update;
    }

    saveChepMiscInfo() {
        if (!this.chepMiscInfoForm.valid) {
            this.chepMiscInfoForm.markAllAsTouched();
            this.messageService.add({
                severity: 'warn',
                summary: 'Could not Update Misc. Info',
                detail: 'Fields are required.'
            });
        } else {
            this.inventoryService.updateInvCountPlantInfo(this.getFormValues()).subscribe(_ => {
                this.messageService.add({
                    severity: 'success',
                    summary: 'Saved Chep Miscellaneous Info',
                    detail: ''
                });
            });
        }
    }

    hasActivity(): boolean {
        return this.sortActivityTypes.length > 0;
    }

    hasPlusActivity() {
        return this.sortActivityTypes.includes(SortActivity.PlusActivity);
    }

    hasPremActivity() {
        return this.sortActivityTypes.includes(SortActivity.PremActivity);
    }

    hasCustActivity() {
        return this.sortActivityTypes.includes(SortActivity.CustActivity);
    }

    hasBlockActivity() {
        return this.sortActivityTypes.includes(SortActivity.BlockRepairs) ;
    }
}
